import React from 'react';
import {
    S36Staff,
    type S36StaffProps,
    type MilanBackgroundColors,
    useBackgroundOptions,
} from '@milanlaser/mlcomponents';
import { ClinicStaffMember, MedicalDirector, Metro, State, Store } from 'clinics/clinics';
import { type PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { processBackgroundColor } from '../../utils/builder.utils';
import { getLocationContext } from '../../../../utils/general/getLocationContext';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state: BuilderState<T>;
    };
};

type S36StaffSectionProps = BuilderProps<{
    metro: Metro;
    state: State;
    store: Store;
    medicalDirectors: MedicalDirector[];
    clinicStaff: ClinicStaffMember[];
}> & {
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    backgroundColor?: MilanBackgroundColors;
};

export function S36StaffSection(props: S36StaffSectionProps) {
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        backgroundColor,
        ...restProps
    } = props;

    const { builderState } = restProps;
    const {
        state: { medicalDirectors, store, clinicStaff },
    } = builderState;
    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;
    const { backgroundStyles } = useBackgroundOptions({
        backgroundColor: processBackgroundColor({
            backgroundColor,
        }),
    });
    const locationName = getLocationContext(builderState);

    const staff: S36StaffProps['staff'] = [
        ...(medicalDirectors ?? []).map(({ displayName, url }) => {
            return {
                name: displayName,
                jobTitle: 'Medical Director',
                image: url,
                imageAltText: `${displayName}, employee at Milan Laser Hair Removal in ${locationName}`,
            };
        }),
        ...(clinicStaff ?? [])
            .filter((member) => member.showOnWebsite)
            .map(({ displayName, position, url }) => {
                return {
                    name: displayName,
                    jobTitle: position,
                    image: url,
                    imageAltText: `${displayName}, employee at Milan Laser Hair Removal in ${locationName}`,
                };
            }),
    ];

    // The Local sites hide the whole section when there is nobody to show — no orphan heading.
    if (staff.length === 0) {
        return null;
    }

    return (
        <section
            className={`mcl-mx-auto mcl-px-4 md:mcl-px-6 xl:mcl-px-4 ${backgroundStyles} ${topPaddingClasses} ${bottomPaddingClasses}`}
        >
            <div className="container">
                <S36Staff locationName={locationName || store.marketingCity} staff={staff} />
            </div>
        </section>
    );
}
